import {remCalc} from '_themeUtils'
import type {ThemeBreakpoints} from '_uiComponents'

import productColorSelector from './partials/product-color-selector'
import productSizeSelector from './partials/product-size-selector'
import productStickybar from './partials/product-stickybar'
import productFindInBoutique from './partials/product-find-in-boutique'
import productContactUs from './partials/product-contact-us'
import productRecommendations from './partials/product-recommendations'
import productStickyAddToCart from './partials/product-sticky-add-to-cart'
import zoomImageModal from './partials/zoom-image-modal'
import productRequestSize from './partials/product-request-size'
import sizeGuide from './partials/size-guide'
import productInfo from './partials/product-info'
import productRelatedSelector from './partials/product-related-selector'

const productDetails = {
    ProductDetails: {
        baseStyle: ({
            headerSize,
            headerSizeCollapsed,
            isHeaderExpanded,
        }: {headerSize?: ThemeBreakpoints; headerSizeCollapsed?: ThemeBreakpoints; isHeaderExpanded?: boolean} = {}) => ({
            container: {
                w: '100%',
                alignItems: 'flex-start',
                mb: 'spacing.15',
                gap: {base: remCalc(20), md: remCalc(80)},
            },

            imagesAndDetailsContainer: {
                w: '100%',
                alignItems: {base: 'center', lg: 'flex-start'},
                flexDirection: {base: 'column', lg: 'row'},
                transition: 'top 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
            },
            // Applied (desktop only) while the image scroll-gate is CLOSED: the whole images+details
            // section is pinned to the top of the viewport under the header, and its height is capped to
            // the visible area so the gallery's vertical Swiper drives the scroll instead of the page.
            // The lg breakpoint mirrors where the desktop row layout kicks in; below lg the object is
            // never applied (the gate is desktop-only) but is kept responsive for safety.
            imagesAndDetailsContainerPinned: {
                position: {base: 'static', lg: 'sticky'},
                top: {
                    base: 0,
                    lg: `${(isHeaderExpanded ? headerSize : headerSizeCollapsed)?.lg ?? 0}px`,
                    xl: `${(isHeaderExpanded ? headerSize : headerSizeCollapsed)?.xl ?? 0}px`,
                    '2xl': `${(isHeaderExpanded ? headerSize : headerSizeCollapsed)?.['2xl'] ?? 0}px`,
                },
                h: {
                    base: 'auto',
                    lg: `calc(100vh - ${(isHeaderExpanded ? headerSize : headerSizeCollapsed)?.lg ?? 0}px)`,
                    xl: `calc(100vh - ${(isHeaderExpanded ? headerSize : headerSizeCollapsed)?.xl ?? 0}px)`,
                    '2xl': `calc(100vh - ${(isHeaderExpanded ? headerSize : headerSizeCollapsed)?.['2xl'] ?? 0}px)`,
                },
                overflow: 'hidden',
            },
            imagesContainer: {
                position: 'relative',
                alignSelf: 'stretch',
                w: {base: '100%', lg: '46%'},
            },
            imagesWrapper: {
                w: '100%',
                position: 'relative',
            },

            detailsContainer: {
                flexGrow: 1,
                w: {base: '100%', lg: '46%'},
                p: {base: `${remCalc(0)} ${remCalc(24)}`, lg: 'unset'},
            },
            detailsWrapper: {
                w: '100%',
                // Mobile & tablet spacing taken from the Figma "Name + price" white section (mobile node
                // 60714:11782 375px artboard; tablet node 60805-38152 768px artboard): 15px top/bottom
                // padding and a 15px gap between the name+price block and the following color/size block.
                // The top padding is 0 because the empty breadcrumb container (hidden below lg) + the
                // wrapper's 15px `gap` already provide the 15px above the product name; adding `pt` would
                // double it. Tablet reuses these base values (its layout mirrors mobile); only desktop (lg)
                // keeps the larger spacing.
                pt: {base: 0, lg: 'spacing.5'},
                pb: {base: remCalc(15), lg: 'spacing.5'},
                pr: {base: '0', lg: remCalc(100)},
                gap: {base: remCalc(15), lg: remCalc(30)},
                alignItems: {base: 'center', md: 'flex-start', lg: 'flex-start'},
            },
            // Wrapper/slot for the mobile "BUY" CTA (full width so the in-flow button can stretch to the
            // content width). The component picks `mobileCtaSlot` while the CTA is 'inflow' — it reserves
            // the button height (button minH 42px + its 8px marginTop) so switching to position:fixed does
            // not leave a blank gap or jump the color section — and `mobileCtaSlotCollapsed` once the
            // button is fixed/hidden, collapsing the reserved space to 0. Following the same
            // theme-variant-per-state pattern as buyButtonInflow/Fixed/Hidden.
            mobileCtaSlot: {
                w: '100%',
                minH: remCalc(50),
            },
            mobileCtaSlotCollapsed: {
                w: '100%',
                minH: 0,
            },
            nameAndPriceContainer: {
                w: '100%',
                alignItems: 'flex-start',
                // Figma "Name + price" (mobile node 60714:11782, tablet node 60805-38152) is literally
                // `padding: 15px 24px; gap: 15px`. The 24px horizontal padding is already provided by
                // detailsContainer, so here we add the 15px top padding (space above the product name) and
                // the 15px name↔price gap. On mobile AND tablet the breadcrumb wrapper above is
                // display:none, so no flex gap precedes this container — this pt is the sole source of the
                // 15px above the name. Desktop (lg) removes the pt and uses a tighter name↔price gap.
                pt: {base: remCalc(8), lg: 0},
                gap: {base: remCalc(12), lg: remCalc(10)},
            },
            breadCrumbContainer: {
                w: '100%',
                alignItems: 'flex-start',
                // The PDP breadcrumb is hidden on mobile AND tablet (Figma tablet node 60805-38152 has no
                // breadcrumb — the white "Name + price" section sits directly under the gallery). This
                // wrapper VStack would otherwise render as an empty flex child, so the detailsWrapper `gap`
                // would add an extra gap above the product name — doubling the space. Collapse it below lg
                // so only one gap remains above the name; it only shows from lg (desktop) up.
                display: {base: 'none', lg: 'flex'},
            },
            colorAndSizeContainer: {
                w: '100%',
                alignItems: 'flex-start',
                gap: remCalc(8),
            },
            boutiqueAndHelpContainer: {
                w: '100%',
                justifyContent: {base: 'flex-start', md: 'center'},
                gap: {base: 0, md: remCalc(60)},
                flexDirection: {base: 'column', md: 'row'},
            },
            productInfoCtasContainer: {
                w: '100%',
                alignItems: 'flex-start',
                gap: 0,
            },
            productInfoCtaButton: {
                w: '100%',
                padding: 0,
                minH: {base: remCalc(40), md: remCalc(32)},
            },
            detailsName: {
                // Figma tablet (node 60805-38152) uses paragraph/sm (14px) for the product name, same as
                // mobile; only desktop (lg) steps up to paragraphMd.
                textStyle: {base: 'paragraphSm', lg: 'paragraphMd'},
                fontWeight: 'normal',
            },
            detailsSizeButtonWrapper: {
                w: '100%',
                justifyContent: 'space-between',
                p: 0,
            },
            detailsSizeButtonText: {
                textStyle: 'paragraphSm',
                fontWeight: 'normal',
                textTransform: 'none',
            },
            ctaButtonsWrapper: {
                display: {base: 'none', lg: 'flex'},
                alignItems: 'center',
                flexDirection: {base: 'row', lg: 'column'},
                gap: 'spacing.2',
                pt: {base: '0', lg: 'spacing.8'},
                w: {base: '100%', lg: '8%'},
            },
            ctaButtonsMobileWrapper: {
                alignItems: 'center',
                // Figma: 15px between the wishlist and share icons on mobile
                // (node 60714:11788 x=0 / 60714:11789 x=35, icons 20px wide → 35 − 20 = 15px).
                gap: remCalc(15),
                justifyContent: 'flex-end',
                ml: 'auto',
            },
            mobilePriceCtaRow: {
                justifyContent: 'space-between',
                w: '100%',
                flexDirection: 'row',
                alignItems: 'center',
            },
            mobilePriceWrapper: {
                flex: '1 1 auto',
                minW: 0,
            },
            detailsInnerRow: {
                alignItems: 'flex-start',
                gap: 0,
            },
            sizeButtonInline: {
                padding: 0,
            },
            quickShareButton: {
                gap: 'spacing.2',
                textStyle: 'paragraphSm',
                textTransform: 'uppercase',
                letterSpacing: remCalc(1.5),
            },
            stickybar: {
                w: 'calc(100% - 60px)',
                mt: '500px',
                position: 'fixed',
                bottom: 'spacing.8',
                left: remCalc(30),
                background: 'white',
                zIndex: 'sticky',

                '&.hidden': {
                    // visibility: 'hidden',
                    display: 'none',
                },
            },
            addToCartButton: {
                marginTop: 'spacing.4',
                textTransform: 'uppercase',
                letterSpacing: remCalc(4),
                alignSelf: {base: 'flex-start', lg: 'center'},
            },
            addToCartButtonSticky: {
                marginTop: 'spacing.4',
                textTransform: 'uppercase',
                letterSpacing: remCalc(4),
                position: 'fixed',
                bottom: remCalc(30),
                zIndex: 'sticky',
            },
            productTag: {
                position: 'absolute',
                zIndex: 'docked',
                top: {base: 'spacing.2', lg: 'spacing.4'},
                left: {base: 'spacing.2', lg: 'spacing.4'},
            },
            mediaCarousel: {
                position: 'relative',
                w: '100%',
                // Mobile: the gallery occupies a FIXED 60% of the total viewport height (60dvh). The
                // remaining 40% is reserved for the accessibility bar + header + the white section with
                // product name/price/buy CTA. Using `dvh` (dynamic viewport height) lets iOS 26 account
                // for its collapsing/expanding navigation bar, so the layout stays stable as that bar
                // resizes instead of jumping with the classic static `vh`.
                //
                // The height is FIXED (no `min()`/clamp): on very tall screens we must NOT shrink the
                // image, otherwise the product name/price and the section below would rise and break the
                // rendering. The image keeps its intrinsic 3/4 ratio (see mediaCarouselSlidePortrait);
                // any overflow is clipped by `mediaCarouselInner` (overflow: hidden), so a taller image is
                // simply cropped / the next slide peeks out rather than being distorted.
                //
                // Tablet (md): same landing mechanism as mobile — the gallery takes a fixed proportion of
                // the viewport height so the white "Name + price" section (name / price+icons / BUY CTA)
                // stays visible at first paint. From the Figma tablet frame (node 60805-38152, 768x1024)
                // the gallery container is 700px of the ~910px below the 114px accessibility+header, i.e.
                // roughly 68% of the viewport; 65dvh keeps the name/price/BUY block on screen while still
                // showing a large image.
                h: {base: '62dvh', md: '70dvh', lg: 'unset'},
                // Short-viewport devices (e.g. iPhone X: usable height ~635px with the browser bars
                // expanded) don't leave enough of the 40% remainder for the fixed-height white block, so
                // the in-flow BUY CTA gets pushed under the browser nav bar. Drop the gallery to 55dvh
                // below a max-height threshold that separates these devices (≈635px) from taller ones
                // (iPhone 17 ~714px), bringing the short-device remainder back in line with what already
                // works on tall devices. `max-height` reacts to the actual layout-viewport height, so it
                // targets the real available area rather than a device model.
                '@media (max-height: 680px)': {
                    height: '60dvh',
                },
                // Very tall phones (e.g. iPhone 17 Pro Max: 932pt logical height) leave a large empty
                // band below the image at the base 62dvh, so the gallery looks too small. Above a
                // min-height threshold that only these 6.9" devices cross (≈900px, clear of the ~844–874pt
                // standard models), grow the gallery to 68dvh — still leaving enough of the remainder for
                // the fixed-height white name/price/BUY block. Uses `min-height` (not a device model) so
                // it reacts to the real layout-viewport height.
                //
                // The `max-width` clause scopes this to the MOBILE range only. A bare `min-height` media
                // query is global and would also match tall tablets (md = 768px, which set 70dvh) and
                // tall desktop windows (lg = unset / intrinsic ratio), overriding both. Capping at
                // 767.98px (just below the md breakpoint) keeps this rule from ever applying at tablet or
                // desktop widths.
                '@media (min-height: 900px) and (max-width: 767.98px)': {
                    height: '69dvh',
                },
                // Desktop keeps the intrinsic aspect ratio to drive its height.
                aspectRatio: {base: 'unset', lg: '3/4'},

                '.swiper-pagination': {
                    position: 'absolute',
                    right: '30px',
                    left: 'auto !important',
                    top: '80px',
                    transform: 'translateY(-50%)',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 'spacing.2',
                    width: 'auto',
                    zIndex: 'docked',
                },
                '.swiper-pagination-bullet': {
                    w: '6px',
                    h: '6px',
                    borderRadius: 'full',
                    bg: '#5F5F5F80',
                    opacity: 1,
                    margin: '0 !important',
                    cursor: 'pointer',
                    transition: 'background-color 0.2s ease, transform 0.2s ease',
                },
                '.swiper-pagination-bullet-active': {
                    bg: '#5F5F5F',
                },
            },
            mediaCarouselInner: {
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                overflow: 'hidden',
            },
            mediaCarouselSlidePortrait: {
                position: 'relative',
                w: '100%',
                aspectRatio: '3/4',
            },
            mediaCarouselSlideLandscape: {
                position: 'relative',
                w: '100%',
                aspectRatio: '16/9',
            },
            mediaCarouselSlideImageWrapper: {
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
            },
            mediaCarouselSlideHoverImage: {
                position: 'absolute',
                top: 0,
                left: 0,
                w: '100%',
                h: '100%',
            },
            shippingMessagesContainer: {
                w: '100%',
                gap: 'spacing.2.5',
                alignItems: 'flex-start',
            },
            shippingMessageContainer: {
                w: '100%',
                gap: 'spacing.1.5',
                // Figma: icon stays top-aligned with the first line of text, even when the text wraps
                // to multiple lines (both mobile and desktop).
                alignItems: 'flex-start',
            },
            shippingMessageAllert: {
                color: 'color.text.default',
                textStyle: 'paragraphSm',
                letterSpacing: 'normal',
                fontWeight: '400',
            },
            appointmentSection: {
                w: '100%',
                gap: remCalc(20),
                textAlign: 'center',
            },
            // Figma mobile (58042-23618): the "Title + description" frame is inset 24px on each side, while
            // the image and CTA stay full-width. Title and description are siblings (no shared wrapper), so
            // the 24px lateral padding is applied to both; desktop keeps the centered maxW layout (no inset).
            appointmentTitle: {
                w: '100%',
                maxW: remCalc(700),
                textStyle: 'paragraphXl',
                fontWeight: 'normal',
                px: {base: remCalc(24), md: 0},
            },
            appointmentDescription: {
                w: '100%',
                maxW: remCalc(700),
                textStyle: 'paragraphSm',
                px: {base: remCalc(24), md: 0},
            },

            appointmentButton: {
                w: 'auto',
                // Cap the width to the container so a long localized copy can't widen the page and cause
                // horizontal scroll. maxW is relative to the full-width appointment section.
                maxW: '100%',
                textStyle: 'buttonXs',
                // Reduced lateral padding (was spacing.14 = 56px, which pushed a long label past the
                // viewport). spacing.6 = 24px keeps the button compact while readable.
                px: 'spacing.6',
                // Allow the label to wrap onto two lines instead of forcing a single overflowing line;
                // the button grows in height (auto) with vertical padding for the wrapped case.
                whiteSpace: 'normal',
                h: 'auto',
                minH: remCalc(48),
                py: 'spacing.3',
                textAlign: 'center',
                mt: 'spacing.5',
            },
            bannerAndAppointmentContainer: {
                w: '100%',
                // No gap between the Page Designer banner section and the appointment section: their
                // spacing is driven by the modules' own spacers, so an extra container gap would add a
                // visible empty band between pdp-banner-section and pdp-appointment-section.
                gap: 0,
                pt: {base: remCalc(10), md: remCalc(30)},
            },
            bannerContainer: {
                w: '100%',
            },
        }),
    },
}

export default {
    ...productDetails,
    ...productColorSelector,
    ...productSizeSelector,
    ...productStickybar,
    ...productFindInBoutique,
    ...productContactUs,
    ...productRecommendations,
    ...productStickyAddToCart,
    ...zoomImageModal,
    ...productRequestSize,
    ...sizeGuide,
    ...productInfo,
    ...productRelatedSelector,
}
